import React, { useState } from 'react'
import CompactSkillMatch from './CompactSkillMatch'
import HoverCombinedDetails from './hover_CombinedDetails'

interface PositionSkill {
  skill_id: number
  skill_name: string
  category: string
  proficiency_level: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  is_good_to_have?: boolean
}

interface PositionCertification {
  certification_name: string
  certification_body?: string
  is_required: boolean
  expiry_required?: boolean
}

interface CandidateSkill {
  uuid?: string
  skill_id?: number
  skill_name?: string
  skillName?: string
  name?: string
  proficiency_level?: string
  proficiencyLevel?: string
  category?: string
  skillCategory?: string
  isPrimarySkill?: boolean
  yearsOfExperience?: number
  certificationRelated?: boolean
}

interface CandidateCertification {
  uuid?: string
  certification_id?: number
  certification_name?: string
  certificationName?: string
  name?: string
  certification_body?: string
  certificationBody?: string
  issuer?: string
  issue_date?: string
  issueDate?: string
  expiry_date?: string
  expiryDate?: string
  is_verified?: boolean
  isVerified?: boolean
  credential_id?: string
  credentialId?: string
  credential_url?: string
  credentialUrl?: string
}

interface HoverSkillMatchContainerProps {
  candidateSkills: CandidateSkill[] | any
  positionSkills: PositionSkill[]
  positionCertifications?: PositionCertification[]
  candidateCertifications?: CandidateCertification[]
  className?: string
  showExpanded?: boolean
  miniMode?: boolean
}

const HoverSkillMatchContainer: React.FC<HoverSkillMatchContainerProps> = ({ 
  candidateSkills, 
  positionSkills, 
  positionCertifications = [],
  candidateCertifications = [],
  className = '',
  showExpanded = false,
  miniMode = false
}) => {
  const [showHover, setShowHover] = useState(false)
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 })

  // Normalize candidate skills to array with robust extraction
  const normalizedCandidateSkills: CandidateSkill[] = (() => {
    if (Array.isArray(candidateSkills)) {
      return candidateSkills
    }
    if (candidateSkills?.skills && Array.isArray(candidateSkills.skills)) {
      return candidateSkills.skills
    }
    if (candidateSkills && typeof candidateSkills === 'object') {
      const possibleSkills = candidateSkills.skillsData || candidateSkills.candidate_skills || candidateSkills.skills
      if (Array.isArray(possibleSkills)) {
        return possibleSkills
      }
    }
    return []
  })()

  // Helper function to normalize skill names for comparison
  const normalizeSkillName = (skillName: string): string => {
    return skillName?.toLowerCase()
      .trim()
      .replace(/[^\w\s]/g, '') // Remove special characters
      .replace(/\s+/g, ' ') // Normalize whitespace
      || ''
  }

  // Helper function to extract skill name from candidate skill object
  const extractCandidateSkillName = (candSkill: any): string => {
    const skillName = candSkill?.skillName || 
                     candSkill?.skill_name || 
                     candSkill?.name || 
                     candSkill?.title ||
                     (typeof candSkill === 'string' ? candSkill : '')
    return normalizeSkillName(skillName)
  }

  // Helper function to check if two skills match
  const skillsMatch = (positionSkill: PositionSkill, candidateSkill: any): boolean => {
    // First try exact ID match
    if (candidateSkill?.skill_id && positionSkill?.skill_id) {
      if (candidateSkill.skill_id === positionSkill.skill_id) {
        return true
      }
    }

    // Then try name matching with normalization
    const posSkillName = normalizeSkillName(positionSkill.skill_name)
    const candSkillName = extractCandidateSkillName(candidateSkill)
    
    if (posSkillName && candSkillName) {
      // Exact match
      if (posSkillName === candSkillName) {
        return true
      }
      
      // Partial match for compound skills (e.g., "React.js" matches "React")
      if (posSkillName.includes(candSkillName) || candSkillName.includes(posSkillName)) {
        return true
      }
      
      // Handle common variations
      const variations: Record<string, string[]> = {
        'javascript': ['js', 'ecmascript'],
        'typescript': ['ts'],
        'react': ['reactjs', 'react.js'],
        'vue': ['vuejs', 'vue.js'],
        'angular': ['angularjs'],
        'node': ['nodejs', 'node.js'],
        'css': ['css3'],
        'html': ['html5'],
        'aws': ['amazon web services'],
        'gcp': ['google cloud platform', 'google cloud'],
        'k8s': ['kubernetes'],
        'docker': ['containerization'],
        'postgresql': ['postgres'],
        'mongodb': ['mongo']
      }
      
      for (const [key, alts] of Object.entries(variations)) {
        if (posSkillName === key && alts.includes(candSkillName)) return true
        if (candSkillName === key && alts.includes(posSkillName)) return true
      }
    }
    
    return false
  }

  // Calculate skill match analysis
  const analyzeSkills = () => {
    if (!positionSkills || positionSkills.length === 0) {
      return { 
        score: 0, 
        percentage: 0, 
        totalPossible: 0,
        requiredMatched: [], 
        requiredMissing: [],
        goodToHaveMatched: [],
        goodToHaveMissing: [],
        candidateExtraSkills: [],
        allCandidateSkills: normalizedCandidateSkills
      }
    }

    const requiredSkills = positionSkills.filter(skill => !skill.is_good_to_have)
    const goodToHaveSkills = positionSkills.filter(skill => skill.is_good_to_have)
    
    let totalPossiblePoints = (requiredSkills.length * 5) + (goodToHaveSkills.length * 1)
    let earnedPoints = 0
    
    const requiredMatched: PositionSkill[] = []
    const requiredMissing: PositionSkill[] = []
    const goodToHaveMatched: PositionSkill[] = []
    const goodToHaveMissing: PositionSkill[] = []

    // Check required skills (5 points each)
    requiredSkills.forEach(posSkill => {
      const hasSkill = normalizedCandidateSkills.some(candSkill => {
        return skillsMatch(posSkill, candSkill)
      })
      
      if (hasSkill) {
        earnedPoints += 5
        requiredMatched.push(posSkill)
      } else {
        requiredMissing.push(posSkill)
      }
    })

    // Check good-to-have skills (1 point each)
    goodToHaveSkills.forEach(posSkill => {
      const hasSkill = normalizedCandidateSkills.some(candSkill => {
        return skillsMatch(posSkill, candSkill)
      })
      
      if (hasSkill) {
        earnedPoints += 1
        goodToHaveMatched.push(posSkill)
      } else {
        goodToHaveMissing.push(posSkill)
      }
    })

    // Find candidate's extra skills (not in position requirements)
    const allPositionSkillNames = positionSkills.map(s => normalizeSkillName(s.skill_name))
    const candidateExtraSkills = normalizedCandidateSkills.filter(candSkill => {
      const skillName = extractCandidateSkillName(candSkill)
      return skillName && !allPositionSkillNames.some(posSkillName => 
        posSkillName === skillName || 
        posSkillName.includes(skillName) || 
        skillName.includes(posSkillName)
      )
    })

    const percentage = totalPossiblePoints > 0 ? Math.round((earnedPoints / totalPossiblePoints) * 100) : 0

    return {
      score: earnedPoints,
      totalPossible: totalPossiblePoints,
      percentage,
      requiredMatched,
      requiredMissing,
      goodToHaveMatched,
      goodToHaveMissing,
      candidateExtraSkills,
      allCandidateSkills: normalizedCandidateSkills
    }
  }

  const skillAnalysis = analyzeSkills()

  const handleMouseEnter = (event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect()
    setHoverPosition({ 
      x: rect.left + rect.width / 2, 
      y: rect.top - 10 
    })
    setShowHover(true)
  }

  const handleMouseLeave = () => {
    setShowHover(false)
  }

  return (
    <div className={`relative ${className}`}>
      {/* Main Content */}
      <div 
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="hover:bg-gray-50 rounded transition-colors"
      >
        <CompactSkillMatch 
          candidateSkills={candidateSkills}
          positionSkills={positionSkills}
          positionCertifications={positionCertifications}
          showExpanded={false}
          miniMode={miniMode}
          disableClick={true}
        />
        
        {/* Certifications indicator (if any) */}
        {positionCertifications.length > 0 && miniMode && (
          <div className="mt-1">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 cursor-default">
              🛡️ {positionCertifications.length} cert{positionCertifications.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* Combined Hover Overlay */}
      {showHover && (
        <div 
          className="fixed z-50 pointer-events-none"
          style={{
            left: `${hoverPosition.x - 160}px`, // Center the 320px wide tooltip
            top: `${hoverPosition.y - 10}px`,
            transform: 'translateY(-100%)'
          }}
        >
          <HoverCombinedDetails 
            skillAnalysis={skillAnalysis}
            positionCertifications={positionCertifications}
            candidateCertifications={candidateCertifications}
          />
        </div>
      )}
    </div>
  )
}

export default HoverSkillMatchContainer